import type { Event, EventInfo } from '../types/api';
import { copyToClipboard } from './eventCalendar';
import { getEventDateTimeDisplay, getEventFormat, getEventLocationLabel, getVenueLabel } from './eventPresentation';
import { buildEventPath } from './slug';

const CANONICAL_SITE = 'https://www.eventfinder.site';

function titleCaseFormat(value: string): string {
  if (value === 'in-person') return 'In-person';
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function buildEventCopyText(event: EventInfo): string {
  const presentationEvent = event as unknown as Event;
  const when = getEventDateTimeDisplay(presentationEvent);
  const dateTime = [
    [when.weekday, when.dateLabel].filter(Boolean).join(', '),
    when.timeLabel,
  ].filter(Boolean).join(' — ');
  const format = titleCaseFormat(getEventFormat(presentationEvent));
  const place = getVenueLabel(presentationEvent) || getEventLocationLabel(presentationEvent);
  const formatLine = place && format !== 'Virtual' ? `${format} — ${place}` : format;
  const primaryHost = event.involvement?.primaryHost;
  const organizations = (event.involvement?.organizations || event.entities || [])
    .filter(entity => !['city', 'industry'].includes(entity.entityType.toLowerCase()))
    .filter(entity => entity.entityId !== primaryHost?.entityId)
    .map(entity => entity.displayName || entity.entityName)
    .join(', ');
  const registrationUrl = event.eventLinks?.find(link => link.kind === 'registration')?.url ||
    event.registrationUrl;
  const eventPageUrl = event.eventLinks?.find(link => link.kind === 'event_page')?.url;
  const path = buildEventPath(event) || `/event/${event.eventId}`;
  const canonicalUrl = `${CANONICAL_SITE}${path}`;

  return [
    event.title,
    dateTime,
    formatLine,
    primaryHost ? `Hosted by: ${primaryHost.displayName || primaryHost.entityName}` : '',
    organizations ? `Organizations at this event: ${organizations}` : '',
    event.description?.trim() || '',
    registrationUrl ? `Register: ${registrationUrl}` : '',
    eventPageUrl ? `Event page: ${eventPageUrl}` : '',
    canonicalUrl,
  ].filter(Boolean).join('\n');
}

export async function copyEventSummary(event: EventInfo): Promise<boolean> {
  return copyToClipboard(buildEventCopyText(event));
}

export { CANONICAL_SITE };